import React, { useMemo } from 'react';
import { Group, Label, Tag, Text as KonvaText } from 'react-konva';
import { useAppSelector } from '../../hooks/redux';
import { pickupReadyFoupsByTool } from '../../features/simulation/toolPickupReady';
import { polygonCentroid } from '../../utils/equipmentGeometry';

type Props = {
  zoomLevel: number;
};

type PickupReadyBadge = {
  id: string;
  x: number;
  y: number;
  labelY: number;
  text: string;
};

/**
 * "FOUP-002 pickup ready" labels on equipment during a live run.
 *
 * A tool is only labelled once the stream says its FOUP finished processing and
 * before the FOUP is picked up, so a tool still processing shows nothing.
 */
const SimulationPickupReadyOverlay: React.FC<Props> = ({ zoomLevel }) => {
  const running = useAppSelector((state) => state.simulation.running);
  const simulationTime = useAppSelector((state) => state.simulation.simulationTime);
  const equipmentInstances = useAppSelector((state) => state.equipment.instances);
  const pickupReady = useAppSelector((state) => state.simulation.live.toolPickupReady);
  const showFoupPickupLabels = useAppSelector(
    (state) => state.ui.showFoupPickupLabels ?? true
  );

  const badges = useMemo(() => {
    if (!running || !showFoupPickupLabels || pickupReady.length === 0) return [] as PickupReadyBadge[];

    const readyByTool = pickupReadyFoupsByTool(pickupReady, simulationTime);
    if (Object.keys(readyByTool).length === 0) return [] as PickupReadyBadge[];

    // The stream names tools by the id sent in the run request, but older
    // layouts and manual edits can leave the display name as the only match.
    const instanceByKey = new Map<string, (typeof equipmentInstances)[string]>();
    for (const instance of Object.values(equipmentInstances)) {
      instanceByKey.set(instance.id, instance);
      if (instance.name) instanceByKey.set(instance.name, instance);
    }

    const out: PickupReadyBadge[] = [];
    for (const [toolId, foupIds] of Object.entries(readyByTool)) {
      const instance = instanceByKey.get(toolId);
      if (!instance?.polygon || instance.polygon.length < 2) continue;
      const centroid = polygonCentroid(instance.polygon);
      out.push({
        id: `pickup-ready-${instance.id}`,
        x: centroid.x,
        y: centroid.y,
        labelY: -28 / zoomLevel,
        text:
          foupIds.length === 1
            ? `${foupIds[0]} pickup ready`
            : `${foupIds.length} FOUPs pickup ready`,
      });
    }
    return out;
  }, [equipmentInstances, pickupReady, running, showFoupPickupLabels, simulationTime, zoomLevel]);

  if (badges.length === 0) return null;

  return (
    <Group listening={false}>
      {badges.map((badge) => (
        <Group key={badge.id} x={badge.x} y={badge.y} listening={false}>
          <Label x={0} y={badge.labelY} listening={false}>
            <Tag
              fill="rgba(15, 23, 42, 0.9)"
              stroke="#22c55e"
              strokeWidth={1 / zoomLevel}
              cornerRadius={4 / zoomLevel}
              pointerDirection="down"
              pointerWidth={8 / zoomLevel}
              pointerHeight={6 / zoomLevel}
            />
            <KonvaText
              text={badge.text}
              fill="#bbf7d0"
              fontSize={8 / zoomLevel}
              fontStyle="bold"
              padding={4 / zoomLevel}
              align="center"
            />
          </Label>
        </Group>
      ))}
    </Group>
  );
};

export default SimulationPickupReadyOverlay;
